// What a named object offers (#2344). Wherever the console names an
// organization, a customer, a site, a device, an SSID or a client, the same
// acts are reached from the name: copy one of its identifiers, make it (or its
// site) the scope, narrow the page to it, and open it. This file is the one
// place that decides which acts a name offers, given the object, the page it is
// named on and the caller; the popover, the phone's sheet, the right-click and
// the search's hits all render what it returns and decide nothing themselves.
//
// It decides nothing the server does not: every act is a URL with a scope or a
// facet (`scope.tsx`, `facets.ts`) or a copy of a value the page already shows,
// so a role is only ever asked here to *hide* a line it could not use (*History*
// for a role that may not open Audit), never to allow one. A line that does not
// apply is absent rather than disabled (`web/PATTERNS.md`, *Permission*).
//
// **No line copies a secret.** An SSID copies its name and its network's ID,
// never a key: revealing a network's key is ADR-0062's act and a site's password
// ADR-0136's, each audited, and neither is reachable from here.
//
// **A device's address is its own**: the `lan_address` a page has read
// (ADR-0391, ADR-0443), and never the address its connection came from, which
// is not a way to reach it and which no page offers to copy.
import type { Lists } from "./estate";
import { facetsOf, homeOf, type Facets } from "./facets";
import type { Scope } from "./scope";

/** An object a page names, with the identifiers the page holds for it. Every
 * field past the first two is optional: a page passes what it has, and a line
 * whose value it does not have is not offered. */
export type Entity =
  | { kind: "org"; id: string; name: string; slug?: string }
  | { kind: "customer"; id: string; name: string; slug?: string; orgId?: string }
  | { kind: "site"; id: string; name: string; slug?: string; orgId?: string; customerId?: string }
  | { kind: "device"; serial: string; name?: string; mac?: string; address?: string; switch?: boolean; siteId?: string; siteName?: string; orgId?: string; customerId?: string;
      /** A serial no site has claimed — a cell's record that belongs to no site,
       * a unit in a warehouse or an import not yet claimed (#2367). It has no
       * device page that holds it, so it has no scope and its menu copies its
       * serial and offers no page to open, no link, no clients and no site. */
      unclaimed?: boolean }
  | { kind: "ssid"; ssid: string; networkId?: string; siteId?: string }
  | { kind: "station"; mac: string; ip?: string; hostname?: string; apSerial?: string; apName?: string; siteId?: string };

export type EntityKind = Entity["kind"];

/** How a kind is named to a person, in a menu's head and a toast. */
export const KIND_WORD: Record<EntityKind, string> = {
  org: "Organization", customer: "Customer", site: "Site", device: "Device", ssid: "SSID", station: "Client",
};

/** Where the name is drawn, and who is looking. */
export interface Here {
  pathname: string;
  scope: Scope;
  /** The estate lists the scope picker reads: where a site's customer and
   * organization are found. Absent while they load. */
  lists?: Lists;
  /** Whether the caller may open Audit anywhere (`audit.read`). */
  mayAudit: boolean;
}

/** A page at a scope with facets. `scope` absent is the scope being looked at. */
export interface Target { pathname: string; facets: Facets; scope?: Scope }

export type Line =
  | { group: "copy"; key: string; label: string; what: string; value: string }
  | { group: "copy"; key: "link"; label: string; what: string; link: Target }
  | { group: "narrow"; key: "scope" | "site-scope"; label: string; value?: string; pick: Scope }
  | { group: "narrow"; key: "filter"; label: string; hint: string; facets: Facets }
  | { group: "go"; key: string; label: string; value?: string; to: Target };

/** The name a person reads for the object. */
export function nameOf(e: Entity): string {
  switch (e.kind) {
    case "device": return e.name || e.serial;
    case "ssid": return e.ssid;
    case "station": return e.hostname || macWords(e.mac);
    default: return e.name;
  }
}

/** The identifier that tells this object apart, as the menu's head shows it. */
export function primaryOf(e: Entity): { what: string; value: string } {
  switch (e.kind) {
    case "device": return { what: "Serial", value: e.serial };
    case "ssid": return { what: "SSID", value: e.ssid };
    case "station": return { what: "MAC", value: macWords(e.mac) };
    default: return { what: "ID", value: e.id };
  }
}

/** Twelve hex digits, however they were written: the facet's spelling (§8.2). */
export function bareMac(mac: string): string {
  return mac.toLowerCase().replace(/[^0-9a-f]/g, "");
}

/** The address a person recognises: aa:bb:cc:00:00:01. */
export function macWords(mac: string): string {
  const b = bareMac(mac);
  return b.length === 12 ? b.match(/../g)!.join(":") : mac;
}

/** The scope a tenancy object is, or a device's site is, from what the entity
 * carries and, failing that, from the estate lists. Undefined where a level
 * above it is not known: a pick needs every level above the one it names. */
export function scopeOf(e: Entity, lists?: Lists): Scope | undefined {
  switch (e.kind) {
    case "org": return { orgId: e.id };
    case "customer": {
      const orgId = e.orgId ?? lists?.customers.find((c) => c.id === e.id)?.org_id;
      return orgId ? { orgId, customerId: e.id } : undefined;
    }
    case "site": return siteScope(e.id, lists, e.orgId, e.customerId);
    // A serial no site has claimed has no scope, whatever site a page says it is for.
    case "device": return e.siteId && !e.unclaimed ? siteScope(e.siteId, lists, e.orgId, e.customerId) : undefined;
    default: return e.siteId ? siteScope(e.siteId, lists) : undefined;
  }
}

function siteScope(siteId: string, lists?: Lists, orgId?: string, customerId?: string): Scope | undefined {
  const row = lists?.sites.find((s) => s.id === siteId);
  const org = orgId ?? row?.org_id;
  const cust = customerId ?? row?.customer_id;
  return org && cust ? { orgId: org, customerId: cust, siteId } : undefined;
}

const same = (a: Scope, b: Scope) => (a.orgId ?? "") === (b.orgId ?? "") && (a.customerId ?? "") === (b.customerId ?? "") && (a.siteId ?? "") === (b.siteId ?? "");

/** Whether the scope being looked at already holds `inner`: a page opened at it
 * shows the object, so a link keeps it rather than moving the person. */
export function holds(scope: Scope, inner: Scope): boolean {
  return (!scope.orgId || scope.orgId === inner.orgId)
    && (!scope.customerId || scope.customerId === inner.customerId)
    && (!scope.siteId || scope.siteId === inner.siteId);
}

/** The site an object is at, by name, where the lists or the entity know it. */
function siteName(e: Entity, lists?: Lists): string | undefined {
  const id = "siteId" in e ? e.siteId : undefined;
  if (!id) return undefined;
  return lists?.sites.find((s) => s.id === id)?.name ?? (e.kind === "device" ? e.siteName : undefined);
}

/** Every act the name offers, grouped Copy, Narrow, Go to, in that order. */
export function entityActions(e: Entity, here: Here): Line[] {
  const lines: Line[] = [];
  const copy = (key: string, what: string, value: string | undefined, label = "Copy " + what.toLowerCase()) => {
    if (value) lines.push({ group: "copy", key, label, what, value });
  };
  const scope = scopeOf(e, here.lists);
  // A link keeps the page's scope where that scope holds the object, and moves
  // to the object's own where it does not: a hit found in the search is often
  // somewhere the page is not looking.
  const at = (t: Omit<Target, "scope">): Target => (scope && !holds(here.scope, scope) ? { ...t, scope } : t);

  let open: Target | undefined;
  switch (e.kind) {
    case "org":
      copy("name", "Name", e.name); copy("id", "ID", e.id, "Copy ID"); copy("slug", "Slug", e.slug);
      break;
    case "customer":
      copy("name", "Name", e.name); copy("id", "ID", e.id, "Copy ID"); copy("slug", "Slug", e.slug);
      break;
    case "site":
      copy("name", "Name", e.name); copy("id", "ID", e.id, "Copy ID"); copy("slug", "Slug", e.slug);
      open = { pathname: `/sites/${e.id}`, facets: {} };
      break;
    case "device":
      copy("name", "Name", e.name); copy("serial", "Serial", e.serial); copy("mac", "MAC", e.mac && macWords(e.mac), "Copy MAC");
      copy("address", "Address", e.address);
      // A serial no site has claimed is on no device page, so there is nothing to open.
      if (!e.unclaimed) open = at({ pathname: homeOf("device", here.pathname).page, facets: { serial: e.serial } });
      break;
    case "ssid":
      copy("ssid", "SSID", e.ssid, "Copy SSID"); copy("network", "Network ID", e.networkId, "Copy network ID");
      open = at({ pathname: homeOf("ssid", here.pathname).page, facets: { ssid: e.ssid } });
      break;
    case "station":
      copy("mac", "MAC", macWords(e.mac), "Copy MAC"); copy("ip", "IP", e.ip, "Copy IP"); copy("hostname", "Hostname", e.hostname);
      open = at({ pathname: homeOf("station", here.pathname).page, facets: { mac: bareMac(e.mac) } });
      break;
  }
  // A link to the object: its own page, or, for an organization or a customer,
  // which have none, this page at its scope.
  const link = open ?? (scope ? { pathname: here.pathname, facets: {}, scope } : undefined);
  if (link) lines.push({ group: "copy", key: "link", label: "Copy link", what: "Link", link });

  // Narrow.
  const tenancy = e.kind === "org" || e.kind === "customer" || e.kind === "site";
  if (tenancy && scope && !same(scope, here.scope)) lines.push({ group: "narrow", key: "scope", label: "Make it the scope", pick: scope });
  const facet = e.kind === "device" ? { serial: e.serial } : e.kind === "ssid" ? { ssid: e.ssid } : e.kind === "station" ? { mac: bareMac(e.mac) } : undefined;
  if (facet && facetsOf(here.pathname).includes(Object.keys(facet)[0] as keyof Facets)) {
    lines.push({ group: "narrow", key: "filter", label: "Show only this here", hint: "⌘-click", facets: facet });
  }
  if (!tenancy && scope && scope.siteId !== here.scope.siteId) {
    lines.push({ group: "narrow", key: "site-scope", label: "Scope to its site", value: siteName(e, here.lists), pick: scope });
  }

  // Go to.
  if (open) lines.push({ group: "go", key: "open", label: "Open", to: open });
  if (e.kind === "device" && !e.switch && !e.unclaimed) lines.push({ group: "go", key: "clients", label: "Its clients", to: at({ pathname: "/operate/wireless", facets: { serial: e.serial } }) });
  if (e.kind === "station" && e.apSerial) {
    lines.push({ group: "go", key: "ap", label: "Its access point", value: e.apName ?? e.apSerial, to: at({ pathname: homeOf("device", here.pathname).page, facets: { serial: e.apSerial } }) });
  }
  const target = e.kind === "org" ? ["org", e.id] : e.kind === "customer" ? ["customer", e.id] : e.kind === "site" ? ["site", e.id]
    : e.kind === "device" ? ["device", e.serial] : e.kind === "ssid" && e.networkId ? ["network", e.networkId] : undefined;
  if (here.mayAudit && target) lines.push({ group: "go", key: "history", label: "History", to: at({ pathname: "/audit", facets: { target_type: target[0], target_id: target[1] } }) });
  return lines;
}
